import React, { useEffect } from "react";

/**
 * The postcard collection, reached from "Gift your panda" on the home page.
 *
 * Visitors were being sent straight to sign-up without ever seeing what a gift
 * looks like, so this page shows the same templates a business picks from in the
 * portal, then repeats the call to action underneath.
 *
 * The artwork lives in public/images/postcards/<key>.jpg; the wording mirrors
 * THEMES in WinterPandaBackend/src/b2b/postcards.ts, which is the source of
 * truth for what the emails actually send.
 */
const TEMPLATES: { key: string; label: string; heading: string; sentiment: string }[] = [
  {
    key: "classic",
    label: "Classic Winter Panda",
    heading: "A Small Gift from Your Team",
    sentiment: "We appreciate your trust and support. We'd love to share a little warmth with you today.",
  },
  {
    key: "christmas",
    label: "Christmas",
    heading: "Merry Christmas",
    sentiment: "Wishing you joy, peace and brighter days ahead.",
  },
  {
    key: "thanksgiving",
    label: "Thanksgiving",
    heading: "With Gratitude",
    sentiment: "Thank you for your continued trust throughout the year.",
  },
  {
    key: "lunar_new_year",
    label: "Lunar New Year",
    heading: "Happy Lunar New Year",
    sentiment: "Wishing you prosperity, health and happiness in the year ahead.",
  },
  {
    key: "easter",
    label: "Easter",
    heading: "Happy Easter",
    sentiment: "Wishing you a bright, peaceful and joyful season.",
  },
  {
    key: "halloween",
    label: "Halloween",
    heading: "Happy Halloween",
    sentiment: "Wishing you a fun, safe and spooky season!",
  },
  {
    key: "thank_you",
    label: "Thank You",
    heading: "Thank You",
    sentiment: "For your support, encouragement and partnership.",
  },
  {
    key: "welcome",
    label: "Welcome",
    heading: "Welcome!",
    sentiment: "We're glad you're here — enjoy a little warmth on us.",
  },
  {
    key: "staff_appreciation",
    label: "Staff Appreciation",
    heading: "Staff Appreciation",
    sentiment: "Thank you for your hard work, dedication and the positive impact you bring every day.",
  },
  {
    key: "client_appreciation",
    label: "Client Appreciation",
    heading: "To Our Valued Clients",
    sentiment: "Thank you for your trust, support and continued partnership.",
  },
  {
    key: "new_release",
    label: "New Panda Updates",
    heading: "Something New Has Arrived",
    sentiment: "A little update — get in touch to find out more!",
  },
];

export default function PostcardsPage({ billingUrl }: { billingUrl: string }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-night-fade font-sans text-frost-100">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-aurora-violet/20 blur-[120px]" />
        <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-aurora-cyan/15 blur-[130px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm text-frost-200/70 transition-colors hover:text-frost-50"
        >
          <span aria-hidden="true">←</span> Back to Winter Panda
        </a>

        <header className="mt-10 text-center">
          <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-frost-200">
            <span className="h-1.5 w-1.5 rounded-full bg-aurora-violet" />
            The postcard collection
          </span>
          <h1 className="mt-6 font-display text-4xl font-semibold tracking-tight text-frost-50 sm:text-5xl">
            Choose how your gift arrives
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-frost-200/80">
            Every Winter Panda arrives as a digital postcard, branded with your name
            and logo. Pick the one that fits the moment — a season, a thank you, or a
            warm welcome — and each recipient gets their own desktop companion with it.
          </p>
        </header>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {TEMPLATES.map((t) => (
            <figure
              key={t.key}
              className="overflow-hidden rounded-[1.75rem] glass shadow-glass transition-transform duration-300 hover:-translate-y-1"
            >
              <img
                src={`/images/postcards/${t.key}.jpg`}
                alt={`${t.heading} — ${t.sentiment}`}
                width={1200}
                height={675}
                loading="lazy"
                className="aspect-[16/9] w-full object-cover"
              />
              <figcaption className="px-5 py-4">
                <p className="font-display text-lg font-semibold text-frost-50">{t.label}</p>
                <p className="mt-1 text-sm leading-relaxed text-frost-200/70">{t.sentiment}</p>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-16 rounded-[2.5rem] glass p-10 text-center shadow-glass sm:p-14">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-frost-50 sm:text-4xl">
            Ready to send one?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-frost-200/80">
            Gift a single panda or warm up a whole team. Each person receives their
            postcard by email and unwraps their own Winter Panda in a click.
          </p>
          <div className="mt-8 flex flex-col items-center">
            <a
              href={billingUrl}
              target="_blank"
              rel="noreferrer"
              className="group relative inline-flex items-center justify-center gap-2.5 overflow-hidden rounded-full px-9 py-4 text-base font-semibold tracking-tight glass text-frost-50 ring-1 ring-white/15 transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10 hover:shadow-[0_20px_50px_-16px_rgba(167,139,250,0.55)] focus:outline-none focus-visible:ring-2 focus-visible:ring-aurora-violet/70"
            >
              <span className="pointer-events-none absolute inset-0 bg-aurora-band bg-[length:200%_200%] opacity-0 transition-opacity duration-300 animate-aurora group-hover:opacity-[0.18]" />
              <span className="relative">🎁 Gift your panda</span>
            </a>
            {/* Same expectation-setting note as the home page. */}
            <p className="mt-3 text-xl text-frost-200/65">By creating an account on the next page</p>
          </div>
        </div>

        <footer className="mt-14 text-center text-xs text-frost-200/45">
          Winter Panda by Winter Desktops ·{" "}
          <a href="/#/legal/privacy" className="hover:text-frost-100">
            Privacy
          </a>{" "}
          ·{" "}
          <a href="/#/legal/terms" className="hover:text-frost-100">
            Terms
          </a>
        </footer>
      </div>
    </div>
  );
}
